import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { history } from '../../../..';
import MaterialTable from 'material-table';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import xss from 'xss';

import { ThemeProvider } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';

import Star from '@material-ui/icons/Star';
import StarBorder from '@material-ui/icons/StarBorder';

import pushToGTM, {
	clickFavouriteDesignGA4Tag,
	CLICK_OPEN_DESIGN_GA4_TAG,
	CLICK_PRODUCT_PAGE_GA4_TAG,
	RENAME_DESIGN_GA4_TAG
} from '../../../../utils/analytics/gtm';
import { tabRoutes } from '../../../../routes';
import { URL } from '../../../../utils/constants';
import { FONT_SIZES } from '../../../../styles/constants';
import theme from './theme';
import icons from './icons';
import { useStyles } from './styles';
import TextField from '../../../TextField';
import Button from '../../../Button';
import Link from '../../../Link';
import Paper from '../../../Paper';
import Chips from '../../../Chips';
import userSlice from '../../../../redux/slices/userSlice';
import selectedDesignSlice from '../../../../redux/slices/selectedDesignSlice';
import tabbarSlice from '../../../Tabbar/tabbarSlice';
import { adobeOpenMyDesign } from '../../../../utils/analytics/adobe/adob';
import { localLogOpenDesign } from '../../../../utils/analytics/local';

const MyDesignsTable = props => {
	const { t } = useTranslation();
	const classes = useStyles();
	const tableRef = useRef(null);
	const [type, setType] = useState('all');
	const [myDesignsLocal, setMyDesignsLocal] = useState([]);
	const userId = useSelector(state => state.user.id);
	const myDesignId = useSelector(state => state.selectedDesign.myDesign.id);
	const myDesigns = useSelector(state => state.user.myDesigns);
	const activeTab = useSelector(state => state.tabbar.activeTab);
	const dispatch = useDispatch();

	const setPage = page => {
		tableRef.current.dataManager.currentPage = page;
	};

	useEffect(() => {
		axios
			.get('api/myDesigns?userId=' + userId)
			.then(response => {
				dispatch(userSlice.actions.setMyDesigns(response.data));
			})
			.catch(() => {});
	}, [dispatch, userId]);

	useEffect(() => {
		setMyDesignsLocal(JSON.parse(JSON.stringify(myDesigns)));
	}, [myDesigns]);

	const handleTypeChange = newType => {
		if (newType !== type) {
			setType(newType);
			setPage(0);
		}
	};

	const setFavorite = (id, favorite) => {
		axios
			.get('api/myDesigns/update/isFavorite?value=' + !favorite + '&id=' + xss.filterXSS(id))
			.then(response => {
				if (response.data) {
					pushToGTM(clickFavouriteDesignGA4Tag(favorite));

					dispatch(
						userSlice.actions.setFavorite({
							id,
							favorite: !favorite
						})
					);
				}
			})
			.catch(() => {
				console.error('Error: setting design ' + id + ' favorite');
			});
	};

	const openMyDesign = (id, rowData) => {
		adobeOpenMyDesign(rowData.name);
		localLogOpenDesign(rowData.name);

		pushToGTM(CLICK_OPEN_DESIGN_GA4_TAG);

		const designRoute = tabRoutes.find(route => route.name === 'Design');

		dispatch(selectedDesignSlice.actions.setOpenMyDesign(true));
		dispatch(selectedDesignSlice.actions.setFetchDesignData(true));
		dispatch(selectedDesignSlice.actions.setMyDesignId(id));
		dispatch(tabbarSlice.actions.enableDesignTabs());

		if (activeTab === designRoute.tabId) {
			dispatch(tabbarSlice.actions.setProgressActive(true));
		} else {
			history.push(designRoute.path);
		}

		props.closeDialog();
	};

	const myDesignsTableColumns = [
		{
			field: 'favorite',
			title: '',
			editable: 'never',
			width: 65,
			render: rowData => (
				<IconButton
					onClick={() => setFavorite(rowData.id, rowData.favorite)}
					disabled={tableRef.current.dataManager.lastEditingRow !== undefined}>
					{rowData.favorite ? <Star className={classes.starIcon} /> : <StarBorder />}
				</IconButton>
			)
		},
		{
			field: 'id',
			title: t('components.navbar.myDesignsTable.columns.id'),
			editable: 'never',
			width: 65
		},
		{
			field: 'name',
			type: 'string',
			title: t('components.navbar.myDesignsTable.columns.name'),
			width: 300,
			editComponent: ({ value, onChange }) => (
				<TextField
					value={value}
					onChange={e => onChange(e.target.value)}
					InputProps={{
						style: {
							fontSize: FONT_SIZES.BODY1
						}
					}}
					inputProps={{ maxLength: 60 }}
				/>
			)
		},
		{
			field: 'icPartNumber',
			type: 'string',
			title: t('components.navbar.myDesignsTable.columns.partNumber'),
			editable: 'never',
			render: ({ icPartNumber }) =>
				tableRef.current.dataManager.lastEditingRow !== undefined ? (
					<span>{icPartNumber}</span>
				) : (
					<Tooltip title={t('components.navbar.myDesignsTable.columns.productDetails')}>
						<div>
							<Link
								variant="body2"
								href={URL.PRODUCT_PAGE_PREFIX + icPartNumber}
								target="_blank"
								onClick={() => {
									pushToGTM(CLICK_PRODUCT_PAGE_GA4_TAG);
								}}>
								{icPartNumber}
							</Link>
						</div>
					</Tooltip>
				)
		},
		{
			field: 'parameters.topology',
			type: 'string',
			title: t('components.navbar.myDesignsTable.columns.topology'),
			editable: 'never',
			render: rowData => t(`topologies:${rowData.parameters.topology}`)
		},
		{
			field: 'parameters.efficiency',
			type: 'numeric',
			title: t('components.navbar.myDesignsTable.columns.efficiency'),
			editable: 'never'
		},
		{
			field: 'parameters.footprint',
			type: 'numeric',
			title: t('components.navbar.myDesignsTable.columns.footprint'),
			editable: 'never'
		},
		{
			field: 'parameters.bomCost',
			type: 'numeric',
			title: t('components.navbar.myDesignsTable.columns.bomCost'),
			editable: 'never'
		},
		{
			field: 'dateCreated',
			title: t('components.navbar.myDesignsTable.columns.dateCreated'),
			editable: 'never',
			defaultSort: 'desc',
			render: rowData => moment(rowData.dateCreated).tz(moment.tz.guess(true)).format('MM/DD/YYYY, hh:mm:ss A Z')
		},
		{
			field: 'dateModified',
			title: t('components.navbar.myDesignsTable.columns.dateModified'),
			editable: 'never',
			render: rowData => moment(rowData.dateModified).tz(moment.tz.guess(true)).format('MM/DD/YYYY, hh:mm:ss A Z')
		},
		{
			field: 'open',
			title: '',
			editable: 'never',
			render: rowData => (
				<Button
					color="primary"
					bold
					disabled={rowData.id === myDesignId || tableRef.current.dataManager.lastEditingRow !== undefined}
					onClick={() => openMyDesign(rowData.id, rowData)}>
					{rowData.id === myDesignId
						? t('components.navbar.myDesignsTable.columns.buttons.opened')
						: t('components.navbar.myDesignsTable.columns.buttons.open')}
				</Button>
			),
			cellStyle: {
				paddingLeft: theme.spacing(2)
			}
		}
	];

	return (
		<ThemeProvider theme={theme}>
			<MaterialTable
				tableRef={tableRef}
				components={{
					Container: containerProps => <Paper elevation={0} noBorder {...containerProps} />
				}}
				icons={icons}
				title={
					<Chips
						value={type}
						updateValue={handleTypeChange}
						chips={[
							{ label: t('components.navbar.myDesignsTable.title.all'), value: 'all' },
							{ label: t('components.navbar.myDesignsTable.title.favorites'), value: 'favorites' }
						]}
					/>
				}
				columns={myDesignsTableColumns}
				data={type === 'all' ? myDesignsLocal : myDesignsLocal.filter(md => md.favorite)}
				localization={{
					header: {
						actions: ''
					},
					body: {
						editRow: {
							deleteText: t('components.navbar.myDesignsTable.deleteText')
						},
						emptyDataSourceMessage: t('components.navbar.myDesignsTable.emptyDataSource'),
						editTooltip: t('components.navbar.myDesignsTable.editTooltip'),
						deleteTooltip: t('components.navbar.myDesignsTable.deleteTooltip')
					},
					pagination: {
						labelRowsPerPage: t('components.navbar.myDesignsTable.rowsPerPage'),
						labelRowsSelect: ''
					}
				}}
				options={{
					actionsColumnIndex: -1,
					doubleHorizontalScroll: true,
					pageSizeOptions: [5, 10, 20]
				}}
				editable={{
					onRowUpdate: (newData, oldData) =>
						new Promise((resolve, reject) => {
							setTimeout(() => {
								if (!newData.name.trim() || oldData.name === newData.name) {
									return resolve();
								}

								pushToGTM(RENAME_DESIGN_GA4_TAG);

								axios
									.get(
										'api/myDesigns/update/name?value=' +
											xss.filterXSS(newData.name) +
											'&id=' +
											xss.filterXSS(oldData.id)
									)
									.then(response => {
										const myDesign = response.data;
										dispatch(
											userSlice.actions.updateMyDesignName({
												id: myDesign.id,
												name: myDesign.name,
												dateModified: myDesign.dateModified
											})
										);
									})
									.catch(() => {
										reject(new Error('Error: updating design ' + oldData.id));
									});

								resolve();
							}, 600);
						}),
					onRowDelete: oldData =>
						new Promise((resolve, reject) => {
							setTimeout(() => {
								axios
									.get('api/myDesigns/?id=' + oldData.id)
									.then(response => {
										if (response.data) {
											dispatch(userSlice.actions.deleteMyDesign(oldData.id));
										}
									})
									.catch(() => {
										reject(new Error('Error: deleting design ' + oldData.id));
									});
								resolve();
							}, 600);
						})
				}}
			/>
		</ThemeProvider>
	);
};

MyDesignsTable.propTypes = {
	closeDialog: PropTypes.func.isRequired
};

export default MyDesignsTable;
